var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Typography, } from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormGroupContextProvider, Loading, useFormGroup, useTranslate, } from 'react-admin';
import { useFormState } from 'react-hook-form';
import { useAuthorizedChildren } from '../useAuthorizedChildren';
/**
 * Renders children (Inputs) inside a MUI <Accordion> element.
 *
 * To be used as child of an <AccordionForm> element.
 *
 * @param {string | ReactNode} label The main label used as the accordion summary. Appears in red when the accordion has errors
 * @param {string | ReactNode} secondary Optional. The secondary label used as the accordion summary
 * @param {string} id Optional. Set an id for this Accordion to be used in the `useFormGroup` hook and for CSS classes
 * @param {boolean} defaultExpanded Optional. Set to true to have the accordion expanded by default (except if autoClose = true on the parent)
 * @param {boolean} disabled Optional. If true, the accordion will be displayed in a disabled state.
 * @param {boolean} square Optional. If true, rounded corners are disabled.
 *
 * @example
 *
 * import { Edit, TextField, TextInput, DateInput, SelectInput, ArrayInput, SimpleFormIterator, BooleanInput } from 'react-admin';
 * import { AccordionForm, AccordionFormPanel } from '@react-admin/ra-form-layout';
 *
 * // don't forget the component="div" prop on the main component to disable the main Card
 * const CustomerEdit = () => (
 *     <Edit component="div">
 *         <AccordionForm>
 *             <AccordionFormPanel label="Identity" defaultExpanded>
 *                 <TextField source="id" />
 *                 <TextInput source="first_name" validate={required()} />
 *                 <TextInput source="last_name" validate={required()} />
 *                 <DateInput source="dob" label="born" validate={required()} />
 *                 <SelectInput source="sex" choices={sexChoices} />
 *             </AccordionFormPanel>
 *             <AccordionFormPanel label="Occupations">
 *                 <ArrayInput source="occupations" label="">
 *                     <SimpleFormIterator>
 *                         <TextInput source="name" validate={required()} />
 *                         <DateInput source="from" validate={required()} />
 *                         <DateInput source="to" />
 *                     </SimpleFormIterator>
 *                 </ArrayInput>
 *             </AccordionFormPanel>
 *             <AccordionFormPanel label="Preferences">
 *                 <SelectInput
 *                     source="language"
 *                     choices={languageChoices}
 *                     defaultValue="en"
 *                 />
 *                 <BooleanInput source="dark_theme" />
 *                 <BooleanInput source="accepts_emails_from_partners" />
 *             </AccordionFormPanel>
 *         </AccordionForm>
 *     </Edit>
 * );
 */
export var AccordionFormPanel = function (props) {
    if (props.id == undefined &&
        typeof props.label !== 'string' &&
        props.autoClose) {
        throw new Error('AccordionFormPanel does not support React Element as label without an id prop. Please provide an id prop.');
    }
    var defaultId = useState(function () { return 'id' + Math.random().toFixed(10).slice(2); })[0];
    var id = props.id !== undefined
        ? props.id
        : typeof props.label === 'string'
            ? props.label
            : defaultId;
    return (_jsx(FormGroupContextProvider, { name: id, children: _jsx(AccordionFormPanelView, __assign({ id: id }, props)) }));
};
var AccordionFormPanelView = function (props) {
    var _a = props.authorizationError, authorizationError = _a === void 0 ? null : _a, children = props.children, count = props.count, _b = props.defaultExpanded, defaultExpanded = _b === void 0 ? false : _b, _c = props.disabled, disabled = _c === void 0 ? false : _c, _d = props.enableAccessControl, enableAccessControl = _d === void 0 ? false : _d, label = props.label, _e = props.loading, loading = _e === void 0 ? DefaultLoading : _e, secondary = props.secondary, id = props.id, square = props.square, 
    // injected by the parent
    _f = props.autoClose, 
    // injected by the parent
    autoClose = _f === void 0 ? false : _f, onChange = props.onChange, expanded = props.expanded, className = props.className, sx = props.sx;
    var _g = useState(defaultExpanded), innerExpanded = _g[0], setExpanded = _g[1];
    var handleChange = function () {
        setExpanded(function (expanded) { return !expanded; });
    };
    var accordionParams = autoClose
        ? {
            expanded: expanded,
            onChange: onChange,
        }
        : {
            expanded: innerExpanded,
            onChange: handleChange,
        };
    var translate = useTranslate();
    var formGroup = useFormGroup(id);
    var isSubmitted = useFormState().isSubmitted;
    var hasErrors = !formGroup.isValid && (formGroup.isTouched || isSubmitted);
    var tabLabel = typeof label === 'string' ? (_jsx(_Fragment, { children: translate(label, { _: label }) })) : (label);
    if (count !== undefined) {
        tabLabel = (_jsxs("span", { children: [tabLabel, " (", count, ")"] }));
    }
    var _h = useAuthorizedChildren({
        children: children,
        enabled: enableAccessControl,
    }), authorizedChildren = _h.authorizedChildren, error = _h.error, isPending = _h.isPending;
    if (error) {
        return authorizationError;
    }
    if (enableAccessControl && isPending) {
        return loading;
    }
    return (_jsxs(StyledAccordion, __assign({}, accordionParams, { disabled: disabled, square: square, className: className, sx: sx, children: [_jsxs(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, {}), "aria-controls": "panel-".concat(id, "-content"), id: "panel-".concat(id, "-header"), children: [_jsx(Typography, { component: "div", className: AccordionFormPanelClasses.heading, sx: {
                            color: hasErrors ? 'error.main' : 'text.primary',
                        }, children: tabLabel }), _jsx(Typography, { component: "div", className: AccordionFormPanelClasses.secondaryHeading, children: secondary && typeof secondary === 'string'
                            ? translate(secondary, { _: secondary })
                            : secondary })] }), _jsx(AccordionDetails, { className: AccordionFormPanelClasses.detail, children: authorizedChildren })] })));
};
var DefaultLoading = _jsx(Loading, {});
var PREFIX = 'RaAccordionFormPanel';
export var AccordionFormPanelClasses = {
    heading: "".concat(PREFIX, "-heading"),
    secondaryHeading: "".concat(PREFIX, "-secondaryHeading"),
    detail: "".concat(PREFIX, "-detail"),
};
var StyledAccordion = styled(Accordion, {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {},
        _b["& .".concat(AccordionFormPanelClasses.heading)] = {
            fontSize: theme.typography.pxToRem(15),
            flexBasis: '33.33%',
            flexShrink: 0,
        },
        _b["& .".concat(AccordionFormPanelClasses.secondaryHeading)] = {
            fontSize: theme.typography.pxToRem(15),
            color: theme.palette.text.secondary,
        },
        _b["& .".concat(AccordionFormPanelClasses.detail)] = {
            alignItems: 'flex-start',
            display: 'flex',
            flexDirection: 'column',
        },
        _b);
});
